/**
 * Material-Palenight-inspiriertes Theme für Monaco.
 *
 * Wir registrieren bewusst KEINE eigene Sprache (`shopify-liquid`) mehr —
 * der eigene Monarch-Tokenizer hat in Verbindung mit Embedded-States
 * (cssEmbed/jsEmbed inkl. Lookahead-Patterns) zu Hängern beim Klick
 * in den Editor geführt. Wir nutzen stattdessen Monacos eingebaute
 * `html`-Sprache (battle-tested) und färben deren Tokens via Theme.
 *
 * Vorteile:
 *  - Stabil, keine Backtracking-Probleme
 *  - HTML/CSS/JS embedded innerhalb von <style>/<script> wird korrekt getokent
 *  - Liquid-Tags/Outputs erscheinen als „normale" HTML-Inhalte ungefährbt —
 *    das ist der akzeptable Kompromiss für ein hängefreies Erlebnis.
 */
import * as monaco from "monaco-editor";

let registered = false;

export function registerShopifyLiquid() {
  if (registered) return;
  registered = true;

  // ════════════════════════════════════════════════════════════════════════
  //  Theme: "floow-shopify" — Material Palenight Palette
  //    bg          #292D3E        Cursor       #FFCC00
  //    fg          #A6ACCD        Comments     #676E95 italic
  //    cyan        #89DDFF        Keywords     cyan italic
  //    purple      #C792EA        Storage      purple italic
  //    pink        #F07178        Tags / Sel.  pink
  //    blue        #82AAFF        Functions    blue
  //    yellow      #FFCB6B        Classes/Ids  yellow
  //    green       #C3E88D        Strings      green
  //    orange      #F78C6C        Numbers/Vals orange
  //    red         #FF5370        true/false/null
  // ════════════════════════════════════════════════════════════════════════
  monaco.editor.defineTheme("floow-shopify", {
    base: "vs-dark",
    inherit: true,
    rules: [
      // Default-Farbe
      { token: "",                           foreground: "A6ACCD" },

      // ── JSON ───────────────────────────────────────────────────────────
      { token: "type.json",                  foreground: "F07178" },
      { token: "string.key.json",            foreground: "F07178" },
      { token: "string.json",                foreground: "C3E88D" },
      { token: "string.value.json",          foreground: "C3E88D" },
      { token: "keyword.json",               foreground: "FF5370", fontStyle: "italic" },
      { token: "number.json",                foreground: "F78C6C" },
      { token: "delimiter.bracket.json",     foreground: "FFCB6B" },
      { token: "delimiter.array.json",       foreground: "FFCB6B" },
      { token: "delimiter.json",             foreground: "89DDFF" },
      { token: "comment.json",               foreground: "676E95", fontStyle: "italic" },

      // ── HTML ───────────────────────────────────────────────────────────
      { token: "tag.html",                   foreground: "F07178" },
      { token: "delimiter.html",             foreground: "89DDFF" },
      { token: "attribute.name.html",        foreground: "C792EA" },
      { token: "attribute.value.html",       foreground: "C3E88D" },
      { token: "attribute.name",             foreground: "C792EA" },
      { token: "attribute.value",            foreground: "C3E88D" },
      { token: "comment.html",               foreground: "676E95", fontStyle: "italic" },
      { token: "metatag.html",               foreground: "FFCB6B" },
      { token: "metatag.content.html",       foreground: "C3E88D" },

      // ── CSS / SCSS / LESS ──────────────────────────────────────────────
      { token: "tag.css",                    foreground: "F07178" },
      { token: "tag.scss",                   foreground: "F07178" },
      { token: "tag.less",                   foreground: "F07178" },
      { token: "attribute.name.css",         foreground: "FFCB6B" },
      { token: "attribute.name.scss",        foreground: "FFCB6B" },
      { token: "attribute.name.less",        foreground: "FFCB6B" },
      { token: "attribute.value.css",        foreground: "F78C6C" },
      { token: "attribute.value.scss",       foreground: "F78C6C" },
      { token: "attribute.value.less",       foreground: "F78C6C" },
      { token: "attribute.value.hex.css",    foreground: "F78C6C" },
      { token: "attribute.value.number.css", foreground: "F78C6C" },
      { token: "attribute.value.unit.css",   foreground: "F78C6C" },
      { token: "number.css",                 foreground: "F78C6C" },
      { token: "number.scss",                foreground: "F78C6C" },
      { token: "string.css",                 foreground: "C3E88D" },
      { token: "string.scss",                foreground: "C3E88D" },
      { token: "string.less",                foreground: "C3E88D" },
      { token: "keyword.css",                foreground: "C792EA", fontStyle: "italic" },
      { token: "keyword.scss",               foreground: "C792EA", fontStyle: "italic" },
      { token: "keyword.less",               foreground: "C792EA", fontStyle: "italic" },
      { token: "variable.css",               foreground: "F78C6C" },
      { token: "variable.scss",              foreground: "F78C6C" },
      { token: "variable.less",              foreground: "F78C6C" },
      { token: "tag.class.css",              foreground: "82AAFF" },
      { token: "tag.id.css",                 foreground: "82AAFF" },
      { token: "comment.css",                foreground: "676E95", fontStyle: "italic" },
      { token: "comment.scss",               foreground: "676E95", fontStyle: "italic" },
      { token: "comment.less",               foreground: "676E95", fontStyle: "italic" },

      // ── JavaScript / TypeScript ────────────────────────────────────────
      { token: "keyword.js",                 foreground: "89DDFF", fontStyle: "italic" },
      { token: "keyword.ts",                 foreground: "89DDFF", fontStyle: "italic" },
      { token: "keyword",                    foreground: "89DDFF", fontStyle: "italic" },
      { token: "string.js",                  foreground: "C3E88D" },
      { token: "string.ts",                  foreground: "C3E88D" },
      { token: "number.js",                  foreground: "F78C6C" },
      { token: "number.ts",                  foreground: "F78C6C" },
      { token: "regexp.js",                  foreground: "F78C6C" },
      { token: "regexp.ts",                  foreground: "F78C6C" },
      { token: "comment.js",                 foreground: "676E95", fontStyle: "italic" },
      { token: "comment.ts",                 foreground: "676E95", fontStyle: "italic" },
      { token: "identifier.js",              foreground: "A6ACCD" },
      { token: "identifier.ts",              foreground: "A6ACCD" },
      { token: "type.identifier.js",         foreground: "FFCB6B" },
      { token: "type.identifier.ts",         foreground: "FFCB6B" },
      { token: "delimiter.js",               foreground: "89DDFF" },
      { token: "delimiter.ts",               foreground: "89DDFF" },
      { token: "delimiter.bracket.js",       foreground: "FFCB6B" },
      { token: "delimiter.bracket.ts",       foreground: "FFCB6B" },
      { token: "delimiter.parenthesis.js",   foreground: "C792EA" },
      { token: "delimiter.parenthesis.ts",   foreground: "C792EA" },
      { token: "delimiter.array.js",         foreground: "C792EA" },
      { token: "delimiter.array.ts",         foreground: "C792EA" },

      // ── Generische Fallbacks ───────────────────────────────────────────
      { token: "string",                     foreground: "C3E88D" },
      { token: "number",                     foreground: "F78C6C" },
      { token: "comment",                    foreground: "676E95", fontStyle: "italic" },
      { token: "operator",                   foreground: "89DDFF" },
      { token: "delimiter",                  foreground: "89DDFF" },
      { token: "delimiter.bracket",          foreground: "FFCB6B" },
      { token: "identifier",                 foreground: "A6ACCD" },
      { token: "type",                       foreground: "FFCB6B" },
    ],
    colors: {
      "editor.background":                       "#292D3E",
      "editor.foreground":                       "#A6ACCD",

      "editorLineNumber.foreground":             "#3A3F58",
      "editorLineNumber.activeForeground":       "#A6ACCD",

      "editor.selectionBackground":              "#717CB450",
      "editor.inactiveSelectionBackground":      "#717CB420",
      "editor.selectionHighlightBackground":     "#717CB430",
      "editor.wordHighlightBackground":          "#717CB430",
      "editor.wordHighlightStrongBackground":    "#717CB440",
      "editor.findMatchBackground":              "#FFCB6B40",
      "editor.findMatchHighlightBackground":     "#FFCB6B20",

      "editor.lineHighlightBackground":          "#00000040",
      "editor.lineHighlightBorder":              "#00000000",

      "editorCursor.foreground":                 "#FFCC00",

      "editorIndentGuide.background":            "#3B3F51",
      "editorIndentGuide.activeBackground":      "#9DA5B4",
      "editorWhitespace.foreground":             "#404660",

      "editorBracketMatch.background":           "#FFCC0030",
      "editorBracketMatch.border":               "#FFCC00",

      "editorWidget.background":                 "#1F2233",
      "editorWidget.border":                     "#3B3F51",
      "editorHoverWidget.background":            "#1F2233",
      "editorHoverWidget.border":                "#3B3F51",
      "editorSuggestWidget.background":          "#1F2233",
      "editorSuggestWidget.border":              "#3B3F51",
      "editorSuggestWidget.selectedBackground":  "#717CB440",
      "editorSuggestWidget.highlightForeground": "#FFCB6B",

      "scrollbarSlider.background":              "#717CB430",
      "scrollbarSlider.hoverBackground":         "#717CB450",
      "scrollbarSlider.activeBackground":        "#717CB470",

      "minimap.background":                      "#292D3E",
      "minimap.selectionHighlight":              "#717CB450",
      "minimapSlider.background":                "#717CB430",
      "minimapSlider.hoverBackground":           "#717CB450",
      "minimapSlider.activeBackground":          "#717CB470",

      "editorGutter.background":                 "#292D3E",
    },
  });
}
